import { Injectable, inject } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { IEnv } from '@tnq/shared/types';
import { ENV } from '@tnq/shared/config';
import {
  IAuthSessionResponse,
  IAuthSessionRequest,
  ILoginRequest,
  IAuthSession,
  IRefreshRequest,
  IRequestPasswordReset,
  IResetPassword,
} from '../interfaces';
import {
  mapLoginRequest,
  mapRefreshRequest,
  mapSessionResponse,
} from '../mappers';
import { ApiService } from './api.service';
import { AuthStore } from '../stores';

const TOKEN_ENDPOINT = 'oauth2/token';
const RESET_PASSWORD_REQUEST_ENDPOINT = 'admin/initiate-reset-password';
const RESET_PASSWORD_ENDPOINT = 'admin/reset-password';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private env = inject<IEnv>(ENV);
  private apiService = inject(ApiService);
  private authStore = inject(AuthStore);

  private isRefreshing = false;

  private get session(): IAuthSession {
    return this.authStore.getSession();
  }

  public login$(request: ILoginRequest): Observable<IAuthSession> {
    const apiRequest: IAuthSessionRequest = mapLoginRequest(
      request,
      this.env.clientId,
      this.env.clientSecret,
    );

    return this.session$(apiRequest);
  }

  public logout$(): Observable<void> {
    this.authStore.removeSession();
    return of();
  }

  public refreshSession$(): Observable<IAuthSession> {
    if (this.isRefreshing) {
      throw new Error('Already refreshing...');
    }
    this.isRefreshing = true;
    const request: IRefreshRequest = {
      refreshToken: this.session.refreshToken,
    };
    const apiRequest: IAuthSessionRequest = mapRefreshRequest(
      request,
      this.env.clientId,
      this.env.clientSecret,
    );

    return this.session$(apiRequest).pipe(
      tap(() => (this.isRefreshing = false)),
    );
  }

  public requestPasswordReset$(
    request: IRequestPasswordReset,
  ): Observable<void> {
    return this.apiService.post$(RESET_PASSWORD_REQUEST_ENDPOINT, request);
  }

  public resetPassword$(resetBody: IResetPassword): Observable<void> {
    return this.apiService.post$(RESET_PASSWORD_ENDPOINT, resetBody);
  }

  private session$(request: IAuthSessionRequest): Observable<IAuthSession> {
    return this.apiService
      .post$<IAuthSessionResponse, IAuthSessionRequest>(TOKEN_ENDPOINT, request)
      .pipe(
        map((response) => mapSessionResponse(response)),
        tap((session) => this.authStore.storeSession(session)),
      );
  }
}
